import { useState, type TransitionEvent } from 'react';

import { useEmbedAPI } from '#cutils/hooks/useEmbedAPI';
import type { KiAssistant } from '#domains/resolver/board-section-data';

import styles from './BoardSectionKiAssistant.module.scss';

type Props = {
	enableViewMode: boolean;
	kiAssistant: KiAssistant;
	className?: string;
};

const VIEW_ID = '-1';

/**
 * The range the embed occupies while minimized. The height committed to the spacer is clamped to
 * it, so a stray value the embed reports mid-collapse can never leave the spacer at a size the
 * minimized embed never actually has.
 */
const MIN_MINIMIZED_HEIGHT = 210;
const MAX_MINIMIZED_HEIGHT = 214;

export function BoardSectionKiAssistant({ kiAssistant, className = '', enableViewMode }: Props) {
	const { isInitialized, height, viewMode, iframeRef } = useEmbedAPI(
		kiAssistant.url,
		`${kiAssistant.rowId}_${VIEW_ID}`,
		{
			type: 'overview',
			id: kiAssistant.boardId,
		},
		{ enableLayoutMode: true }
	);

	const isMaximized = enableViewMode && viewMode === 'maximized';

	const [minimizedHeight, setMinimizedHeight] = useState(MAX_MINIMIZED_HEIGHT);

	const url = new URL(kiAssistant.url);
	url.searchParams.append('viewId', VIEW_ID);

	// the height is only known after the embed API has initialized, so we use a default height until then to avoid a jump in the layout.
	// the minimized-height clamp only makes sense while view mode is active — otherwise just use the height the embed reports.
	function getIframeHeight() {
		if (typeof height !== 'number' || !isInitialized) {
			return MAX_MINIMIZED_HEIGHT;
		}
		return enableViewMode ? Math.max(height, MAX_MINIMIZED_HEIGHT) : height;
	}

	const iframeHeight = getIframeHeight();

	// while minimizing, the embed reports several intermediate heights. Each one restarts the
	// iframe's height transition, so `transitionend` only fires once the embed has settled —
	// keeping the spacer off every mid-transition frame.
	function handleTransitionEnd(event: TransitionEvent<HTMLIFrameElement>) {
		if (event.propertyName === 'height' && !isMaximized && typeof height === 'number') {
			setMinimizedHeight(Math.min(Math.max(height, MIN_MINIMIZED_HEIGHT), MAX_MINIMIZED_HEIGHT));
		}
	}

	return (
		<>
			{enableViewMode ? <div aria-hidden="true" className={styles.spacer} style={{ height: minimizedHeight }} /> : null}
			<iframe
				ref={iframeRef}
				data-testid="ki-assistant"
				title={kiAssistant.altText || 'Embed'}
				frameBorder="0"
				src={url.toString()}
				className={`${className} ${styles.iframe} ${enableViewMode ? `${styles.desktop} ` : ''}`}
				style={isMaximized ? undefined : { height: iframeHeight }}
				onTransitionEnd={handleTransitionEnd}
			/>
		</>
	);
}
